import React, { useState, useRef } from "react";
import * as Yup from "yup";
import { useOutletContext } from "react-router-dom";
import { OutletContextType } from "../../types/GlobalType";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { parentController } from "../../_api/controllers/parent.controller.ts";
import {
  promotionController,
  schoolYearController,
} from "../../_api/controllers/scolarite.controller.ts";
import { studentControler } from "../../_api/controllers/student.controller.ts";
import { capitalizeFirstLetter } from "../../_services/utils";
import z from "zod";
import { createParentSchema } from "../../validators/parent.schema.ts";

// parent skeleton component
const ParentsPageSkeleton = ({
  isDarkMode,
  rows = 6,
}: {
  isDarkMode: boolean;
  rows?: number;
}) => {
  const bgCard = isDarkMode ? "bg-[#161B22]" : "bg-white";
  const bgPage = isDarkMode ? "bg-[#0D1117]" : "bg-white";
  const border = isDarkMode ? "border-[#30363D]" : "border-gray-200";
  const skeleton = "bg-gray-300 dark:bg-[#30363D]";

  return (
    <div className="space-y-4 animate-pulse">
      <div className={`rounded-xl overflow-hidden border ${border}`}>
        <div className={`p-5 ${bgPage}`}>
          {/* ===== STATS ===== */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 mb-5">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className={`p-4 rounded-lg border ${bgCard} ${border}`}
              >
                <div className="flex justify-between items-center">
                  <div className="space-y-2">
                    <div className={`h-3 w-28 rounded ${skeleton}`} />
                    <div className={`h-7 w-20 rounded ${skeleton}`} />
                  </div>
                  <div className={`h-12 w-12 rounded-full ${skeleton}`} />
                </div>
              </div>
            ))}
          </div>

          {/* ===== SEARCH & FILTER ===== */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className={`h-10 flex-grow rounded ${skeleton}`} />
            <div className={`h-10 w-40 rounded ${skeleton}`} />
            <div className={`h-10 w-48 rounded ${skeleton}`} />
          </div>

          {/* ===== TABLE ===== */}
          <div className={`rounded-xl overflow-hidden border ${border}`}>
            {/* Table header */}
            <div
              className={`grid grid-cols-5 gap-4 px-6 py-3 ${
                isDarkMode ? "bg-[#161B22]" : "bg-white"
              }`}
            >
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className={`h-3 rounded ${skeleton}`} />
              ))}
            </div>

            {/* Rows */}
            <div className={`${bgPage}`}>
              {[...Array(rows)].map((_, i) => (
                <div
                  key={i}
                  className={`grid grid-cols-5 gap-4 px-5 py-3 border-t ${border}`}
                >
                  {/* Name */}
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-full ${skeleton}`} />
                    <div className="space-y-2">
                      <div className={`h-3 w-32 rounded ${skeleton}`} />
                      <div className={`h-2 w-20 rounded ${skeleton}`} />
                    </div>
                  </div>

                  <div className={`h-3 w-24 rounded ${skeleton}`} />
                  <div className={`h-5 w-20 rounded-full ${skeleton}`} />
                  <div className={`h-5 w-16 rounded-full ${skeleton}`} />

                  {/* Actions */}
                  <div className="flex justify-center gap-3">
                    <div className={`h-5 w-5 rounded ${skeleton}`} />
                    <div className={`h-5 w-5 rounded ${skeleton}`} />
                    <div className={`h-5 w-5 rounded ${skeleton}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ===== PAGINATION ===== */}
          <div className="mt-4">
            <div className={`h-3 w-48 rounded ${skeleton}`} />
          </div>
        </div>
      </div>
    </div>
  );
};

// Small helper for the read-only info rows shown in the detail popup
const DetailField = ({
  label,
  value,
  isDarkMode,
  icon,
}: {
  label: string;
  value: React.ReactNode;
  isDarkMode: boolean;
  icon?: string;
}) => (
  <div>
    <p
      className={`text-xs font-medium mb-0.5 ${
        isDarkMode ? "text-gray-500" : "text-gray-400"
      }`}
    >
      {label}
    </p>
    <p
      className={`text-sm flex items-center gap-1.5 ${
        isDarkMode ? "text-gray-100" : "text-gray-900"
      }`}
    >
      {icon && <i className={`fas ${icon} text-xs opacity-60`}></i>}
      {value || "-"}
    </p>
  </div>
);

const parentTypeLabel: Record<string, string> = {
  pere: "Père",
  mere: "Mère",
  tuteur: "Tuteur légal",
  autre: "Autre",
};

const Parent: React.FC = () => {
  const { isDarkMode, setIsDarkMode } = useOutletContext<OutletContextType>();
  const [notification, setNotification] = useState<any>(null); // { message, type }
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState<any>(false); // { message, type }
  const showNotification = (message: string, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Detail popup (parent + ses enfants)
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedParent, setSelectedParent] = useState<any>(null);

  const handleRowClick = (item: any) => {
    setSelectedParent(item);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedParent(null);
  };

  //filter parent
  const [searchQuery, setSearchQuery] = useState("");
  const [filtrerParentStatus, setfiltrerParentStatus] =
    useState<any>("Tous les status");
  const [isParentStatusDropdownOpen, setIsParentStatusDropdownOpen] =
    useState(false);
  const toggleStatusDropdown = () => {
    setIsParentStatusDropdownOpen(!isParentStatusDropdownOpen);
  };

  const parentStatusDropdownRef = useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      //status button
      if (
        parentStatusDropdownRef.current &&
        !parentStatusDropdownRef.current.contains(event.target as Node)
      ) {
        setIsParentStatusDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  //UseReducer for Desactivate, Activate
  //handleling delete & activate & desactivate items
  interface ActionState {
    actionSection: string;
    actionType: string;
    actionText: string;
    currentId: any;
  }

  interface Action {
    type: "DELETE" | "DESACTIVATE" | "CANCEL" | "ACTIVATE";
    payload: { actionText: string; actionSection: string; currentId: any };
  }
  const ACTION_TYPES = {
    DELETE: "DELETE",
    DESACTIVATE: "DESACTIVATE",
    ACTIVATE: "ACTIVATE",
    CANCEL: "CANCEL",
  };

  const INITIAL_STATE: ActionState = {
    actionSection: "",
    actionType: "",
    actionText: "",
    currentId: null,
  };

  const actionReducer = (state: ActionState, action: Action): ActionState => {
    switch (action.type) {
      case ACTION_TYPES.DELETE:
        return {
          ...state,
          actionType: "delete",
          actionSection: action.payload.actionSection,
          actionText: action.payload.actionText,
          currentId: action.payload.currentId,
        };

      case ACTION_TYPES.ACTIVATE:
        return {
          ...state,
          actionType: "activate",
          actionSection: action.payload.actionSection,
          actionText: action.payload.actionText,
          currentId: action.payload.currentId,
        };
      case ACTION_TYPES.DESACTIVATE:
        return {
          ...state,
          actionType: "desactivate",
          actionSection: action.payload.actionSection,
          actionText: action.payload.actionText,
          currentId: action.payload.currentId,
        };
      case ACTION_TYPES.CANCEL:
        return {
          actionSection: "",
          actionType: "",
          actionText: "",
          currentId: null,
        };

      default:
        return state;
    }
  };

  const [state_actions, dispatch] = React.useReducer(
    actionReducer,
    INITIAL_STATE,
  );

  const queryClient = useQueryClient();

  //fetch all users
  const { data: parentsData, isLoading: isParentsLoading } = useQuery({
    queryKey: ["parents"],
    queryFn: parentController.getAll,
  });

  //fetch eleves
  const { data: studentData, isLoading: isStudentsLoading } = useQuery({
    queryKey: ["students"],
    queryFn: studentControler.getAll,
  });

  //fetch promotion
  const { data: promotionsData, isLoading: isPromotionsLoading } = useQuery({
    queryKey: ["promotions"],
    queryFn: promotionController.getAll,
  });
  //fetch anne scolaire
  const { data: schoolYearData, isLoading: isSchoolYearLoading } = useQuery({
    queryKey: ["schoolYears"],
    queryFn: schoolYearController.getAll,
  });

  const studentDataArr = studentData?.data || [];
  const promotionsDataArr = promotionsData?.data || [];
  const parentsDataArr = parentsData?.data || [];
  const schoolYearDataArr = schoolYearData?.data || [];

  const activeShoolYear = schoolYearDataArr.filter((item) => item.status);

  const updateParentsArr = parentsDataArr.map((parent) => {
    let students = [];
    let activeStudents = [];

    for (let student of studentDataArr) {
      if (parent.parent_id === student.parent_id) {
        students.push(student);
      }
    }

    for (let promo of promotionsDataArr) {
      for (let item of students) {
        if (
          promo.eleve_id === item.eleve_id &&
          promo.annee_scolaire_id === activeShoolYear[0]?.id
        ) {
          activeStudents.push(item);
        }
      }
    }
    return {
      ...parent,
      students: activeStudents,
    };
  });

  const filteredParents = updateParentsArr.filter((parent) => {
    const matchesQuery =
      parent.nom.toLowerCase().includes(searchQuery.toLowerCase()) ||
      parent.postnom.toLowerCase().includes(searchQuery.toLowerCase()) ||
      parent.prenom.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStudentStatus =
      filtrerParentStatus === "Tous les status" ||
      filtrerParentStatus == parent.status;

    return matchesQuery && matchesStudentStatus;
  });

  //statistic number
  let parentStats = {
    total: updateParentsArr.length,
    active: updateParentsArr.filter((item) => item.status).length,
    inactive: updateParentsArr.filter((item) => !item.status).length,
  };

  const [formNewData, setformNewData] = useState<{
    nom: string;
    postnom: string;
    prenom: string;
    sexe: string;
    ville: string;
    commune: string;
    quartier: string;
    avenue: string;
    telephone: string;
    email: string;
    profession: string;
    type: string;
  }>({
    nom: "",
    postnom: "",
    prenom: "",
    sexe: "",
    ville: "",
    commune: "",
    quartier: "",
    avenue: "",
    telephone: "",
    email: "",
    profession: "",
    type: "",
  });
  const [formEditData, setformEditData] = useState<{
    id: any;
    nom: string;
    postnom: string;
    prenom: string;
    sexe: string;
    ville: string;
    commune: string;
    quartier: string;
    avenue: string;
    telephone: string;
    email: string;
    profession: string;
    type: string;
  }>({
    id: null,
    nom: "",
    postnom: "",
    prenom: "",
    sexe: "",
    ville: "",
    commune: "",
    quartier: "",
    avenue: "",
    telephone: "",
    email: "",
    profession: "",
    type: "",
  });

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    showEditModal
      ? setformEditData((prev) => ({ ...prev, [name]: value }))
      : setformNewData((prev) => ({ ...prev, [name]: value }));
  };

  // Ouvre le formulaire d'édition pré-rempli, réutilisée par la ligne du
  // tableau ET par le popup de détail (aucune nouvelle logique : mêmes
  // setters que ceux déjà utilisés sur le bouton "éditer" de la table).
  const openEditForParent = (item: any) => {
    setformEditData({
      id: item.parent_id,
      nom: item.nom,
      postnom: item.postnom,
      prenom: item.prenom,
      sexe: item.sexe,
      ville: item.ville,
      commune: item.commune,
      quartier: item.quartier,
      avenue: item.avenue,
      telephone: item.telephone,
      email: item.email,
      profession: item.profession,
      type: item.type,
    });
    setShowEditModal(true);
  };

  const mutationUpdate = useMutation({
    mutationFn: () =>
      parentController.update(formEditData.id, {
        nom: formEditData.nom,
        postnom: formEditData.postnom,
        prenom: formEditData.prenom,
        sexe: formEditData.sexe,
        ville: formEditData.ville,
        commune: formEditData.commune,
        quartier: formEditData.quartier,
        avenue: formEditData.avenue,
        telephone: formEditData.telephone,
        email: formEditData.email,
        profession: formEditData.profession,
        type: formEditData.type,
      }),

    onSuccess: () => {
      setShowEditModal(false);
      showNotification("Modification effectuée avec succès", "success");
      setformEditData({
        id: null,
        nom: "",
        postnom: "",
        prenom: "",
        sexe: "",
        ville: "",
        commune: "",
        quartier: "",
        avenue: "",
        telephone: "",
        email: "",
        profession: "",
        type: "",
      });
      queryClient.invalidateQueries({ queryKey: ["parents"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });
  const mutationCreate = useMutation({
    mutationFn: () =>
      parentController.create({
        nom: formNewData.nom,
        postnom: formNewData.postnom,
        prenom: formNewData.prenom,
        sexe: formNewData.sexe,
        ville: formNewData.ville,
        commune: formNewData.commune,
        quartier: formNewData.quartier,
        avenue: formNewData.avenue,
        telephone: formNewData.telephone,
        email: formNewData.email,
        profession: formNewData.profession,
        type: formNewData.type,
      }),

    onSuccess: () => {
      setShowAddModal(false);
      showNotification("Modification effectuée avec succès", "success");
      setformNewData({
        nom: "",
        postnom: "",
        prenom: "",
        sexe: "",
        ville: "",
        commune: "",
        quartier: "",
        avenue: "",
        telephone: "",
        email: "",
        profession: "",
        type: "",
      });
      queryClient.invalidateQueries({ queryKey: ["parents"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationDesactivate = useMutation({
    mutationFn: () => parentController.desactivate(state_actions.currentId),

    onSuccess: (data) => {
      // console.log(data);
      showNotification("Désactivation effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });
      // setCurrentId(null);
      queryClient.invalidateQueries({ queryKey: ["parents"] });
    },

    onError: (err) => {
      // console.log(err);
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationActivate = useMutation({
    mutationFn: () => parentController.activate(state_actions.currentId),

    onSuccess: (data) => {
      showNotification("Activation effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });
      // setCurrentId(null);
      queryClient.invalidateQueries({ queryKey: ["parents"] });
    },

    onError: (err: any) => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const handleActivate = () => {
    if (
      state_actions.actionType === "activate" &&
      state_actions.actionSection === "parent"
    ) {
      mutationActivate.mutate();
    }
  };

  const handleDesactivate = () => {
    if (
      state_actions.actionType === "desactivate" &&
      state_actions.actionSection === "parent"
    ) {
      mutationDesactivate.mutate();
    }
  };

  const parentFormFields = [
    "nom",
    "postnom",
    "prenom",
    "type",
    "sexe",
    "avenue",
    "quartier",
    "commune",
    "ville",
    "telephone",
    "email",
    "profession",
  ] as const;

  const handleUpdate = async () => {
    console.log("Edit / update parent");
    console.log(formEditData);
    const zodValidation = createParentSchema.safeParse(formEditData);

    if (!zodValidation.success) {
      const z_errors = z.treeifyError(zodValidation.error);

      const firstError = parentFormFields
        .map((field) => z_errors.properties?.[field]?.errors?.[0])
        .find(Boolean);

      return showNotification(firstError ?? "Erreur de validation", "erreur");
    }
    mutationUpdate.mutate();
  };
  const handleCreate = async () => {
    const zodValidation = createParentSchema.safeParse(formNewData);

    if (!zodValidation.success) {
      const z_errors = z.treeifyError(zodValidation.error);

      const firstError = parentFormFields
        .map((field) => z_errors.properties?.[field]?.errors?.[0])
        .find(Boolean);

      return showNotification(firstError ?? "Erreur de validation", "erreur");
    }

    mutationCreate.mutate();
  };

  // Classes de présentation alignées sur la page Student de référence.
  const parentCardClass = `overflow-hidden rounded-2xl border shadow-sm ${isDarkMode ? "border-[#30363D] bg-[#0D1117] shadow-black/10" : "border-gray-200 bg-white shadow-gray-200/40"}`;
  const parentInputClass = `h-9 rounded-lg border text-[11px] outline-none transition-all ${isDarkMode ? "border-[#30363D] bg-[#0D1117] text-gray-200 placeholder:text-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10" : "border-gray-200 bg-white text-gray-800 placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"}`;
  const parentFilterClass = `inline-flex h-9 items-center gap-2 rounded-lg border px-3 text-[10px] font-medium transition-colors ${isDarkMode ? "border-[#30363D] bg-[#161B22] text-gray-300 hover:bg-[#21262D]" : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"}`;
  const parentActionClass = (tone: "green" | "blue") =>
    `inline-flex h-8 w-8 items-center justify-center rounded-lg border transition-all duration-150 ${isDarkMode ? "border-[#30363D] " + (tone === "blue" ? "text-blue-400 hover:bg-blue-500/10" : "text-emerald-400 hover:bg-emerald-500/10") : "border-gray-200 " + (tone === "blue" ? "text-blue-600 hover:bg-blue-50" : "text-emerald-600 hover:bg-emerald-50")}`;

  const statusPill = (
    active: boolean,
    labelActive: string,
    labelInactive: string,
  ) => (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[9px] font-semibold ${
        active
          ? isDarkMode
            ? "bg-emerald-500/10 text-emerald-400"
            : "bg-emerald-50 text-emerald-600"
          : isDarkMode
            ? "bg-red-500/10 text-red-400"
            : "bg-red-50 text-red-600"
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          active ? "bg-emerald-500" : "bg-red-500"
        }`}
      ></span>
      {active ? labelActive : labelInactive}
    </span>
  );
  return (
    <>
      {isParentsLoading ||
      isStudentsLoading ||
      isPromotionsLoading ||
      isSchoolYearLoading ? (
        <ParentsPageSkeleton isDarkMode={isDarkMode} />
      ) : (
        <div className="min-w-0 w-full space-y-4">
          <div className={parentCardClass}>
            <div
              className={`p-4 sm:p-5 ${isDarkMode ? "bg-[#0D1117] text-gray-200" : "bg-white text-gray-800"}`}
            >
              {/* Stats Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                <div
                  className={`p-3.5 sm:p-4 rounded-xl transition-all hover:shadow-sm ${
                    isDarkMode ? "bg-[#161B22]" : "bg-white"
                  } border ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p
                        className={`text-[11px] font-medium ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Total Parents
                      </p>
                      <h3 className="text-xl font-bold mt-1 tracking-tight">
                        {parentStats.total}
                      </h3>
                    </div>
                    <div
                      className={`h-9 w-9 flex items-center justify-center rounded-lg ${
                        isDarkMode ? "bg-blue-500/10" : "bg-blue-50"
                      }`}
                    >
                      <i
                        className={`fas fa-users ${
                          isDarkMode ? "text-blue-400" : "text-blue-600"
                        }`}
                      ></i>
                    </div>
                  </div>
                </div>
                <div
                  className={`p-3.5 sm:p-4 rounded-xl transition-all hover:shadow-sm ${
                    isDarkMode ? "bg-[#161B22]" : "bg-white"
                  } border ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p
                        className={`text-[11px] font-medium ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Parents actifs
                      </p>
                      <h3 className="text-xl font-bold mt-1 tracking-tight">
                        {parentStats.active}
                      </h3>
                    </div>
                    <div
                      className={`h-9 w-9 flex items-center justify-center rounded-lg ${
                        isDarkMode ? "bg-emerald-500/10" : "bg-emerald-50"
                      }`}
                    >
                      <i
                        className={`fas fa-user-check ${
                          isDarkMode ? "text-green-400" : "text-green-600"
                        }`}
                      ></i>
                    </div>
                  </div>
                </div>
                <div
                  className={`p-3.5 sm:p-4 rounded-xl transition-all hover:shadow-sm ${
                    isDarkMode ? "bg-[#161B22]" : "bg-gray-50"
                  } border ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p
                        className={`text-[11px] font-medium ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Parents inactifs
                      </p>
                      <h3 className="text-xl font-bold mt-1 tracking-tight">
                        {parentStats.inactive}
                      </h3>
                    </div>
                    <div
                      className={`h-9 w-9 flex items-center justify-center rounded-lg ${
                        isDarkMode ? "bg-gray-800" : "bg-gray-200"
                      }`}
                    >
                      <i
                        className={`fas fa-archive ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      ></i>
                    </div>
                  </div>
                </div>
              </div>
              {/* Search and Filters */}
              <div className="flex flex-wrap items-center gap-2.5 mb-5">
                <div className="flex-grow relative min-w-[220px]">
                  <div
                    className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none ${
                      isDarkMode ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    <i className="fas fa-search"></i>
                  </div>
                  <input
                    type="text"
                    placeholder="Rechercher un parent..."
                    className={`${parentInputClass} w-full pl-10 pr-4`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="relative">
                  <button
                    onClick={toggleStatusDropdown}
                    className={`${parentFilterClass} whitespace-nowrap`}
                  >
                    <span>
                      {filtrerParentStatus == 1
                        ? "Active"
                        : filtrerParentStatus == 0
                          ? "Désactivé"
                          : "Tous les status"}
                    </span>
                    <i className="fas fa-chevron-down text-xs opacity-60"></i>
                  </button>
                  {isParentStatusDropdownOpen && (
                    <div
                      className={`absolute top-full left-0 mt-1.5 w-40 rounded-xl shadow-xl overflow-hidden ${
                        isDarkMode ? "bg-[#161B22]" : "bg-white"
                      } border ${
                        isDarkMode ? "border-[#30363D]" : "border-gray-200"
                      } z-50`}
                      ref={parentStatusDropdownRef}
                    >
                      <div className="py-1">
                        {[
                          {
                            id: 0,
                            designation: "Tous les status",
                            status: "Tous les status",
                          },
                          { id: 1, designation: "Active", status: 1 },
                          { id: 2, designation: "Désactivé", status: 0 },
                        ].map((status) => (
                          <button
                            key={status.id}
                            onClick={() => {
                              setfiltrerParentStatus(status.status);
                              setIsParentStatusDropdownOpen(false);
                            }}
                            className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                              isDarkMode
                                ? "hover:bg-[#21262D] text-gray-200"
                                : "hover:bg-gray-100 text-gray-700"
                            } !rounded-button whitespace-nowrap`}
                          >
                            {status.designation}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    setShowAddModal(true);
                  }}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-medium transition-colors shadow-sm ${
                    isDarkMode
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-green-500 hover:bg-green-600"
                  } text-white !rounded-button whitespace-nowrap`}
                >
                  <i className="fas fa-plus"></i>Nouveau parent
                </button>
              </div>
              {/* Parents Table */}
              <div
                className={`rounded-xl overflow-auto border ${
                  isDarkMode ? "border-[#30363D]" : "border-gray-200"
                }`}
              >
                <table
                  className={`w-full min-w-[760px] text-xs divide-y ${isDarkMode ? "divide-[#30363D]" : "divide-gray-200"}`}
                >
                  <thead
                    className={`${isDarkMode ? "bg-[#161B22]" : "bg-gray-50"}`}
                  >
                    <tr>
                      <th
                        scope="col"
                        className={`px-4 py-3 text-left text-[9px] font-semibold uppercase tracking-[0.08em] ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Nom & Prénom
                      </th>
                      <th
                        scope="col"
                        className={`px-4 py-3 text-left text-[9px] font-semibold uppercase tracking-[0.08em] ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Téléphone
                      </th>
                      <th
                        scope="col"
                        className={`px-4 py-3 text-left text-[9px] font-semibold uppercase tracking-[0.08em] ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Enfants actifs
                      </th>

                      <th
                        scope="col"
                        className={`px-4 py-3 text-center text-[9px] font-semibold uppercase tracking-[0.08em] ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      ></th>
                    </tr>
                  </thead>
                  <tbody
                    className={`${
                      isDarkMode ? "bg-[#0D1117]" : "bg-white"
                    } divide-y ${
                      isDarkMode ? "divide-[#21262D]" : "divide-gray-100"
                    }`}
                  >
                    {filteredParents.length === 0 && (
                      <tr>
                        <td
                          colSpan={5}
                          className={`px-4 py-12 text-center text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          Aucun parent ne correspond aux critères de recherche.
                        </td>
                      </tr>
                    )}
                    {filteredParents
                      .sort((a, b) =>
                        a.nom.toLowerCase().localeCompare(b.nom.toLowerCase()),
                      )
                      .map((item, index) => (
                        <tr
                          key={item.id}
                          onClick={() => handleRowClick(item)}
                          className={`group cursor-pointer transition-colors ${
                            isDarkMode
                              ? "hover:bg-[#161B22]"
                              : "hover:bg-gray-50"
                          }`}
                        >
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-9 w-9">
                                <div
                                  className={`h-9 w-9 rounded-xl flex items-center justify-center ${
                                    isDarkMode ? "bg-[#30363D]" : "bg-gray-200"
                                  }`}
                                >
                                  <i className="fas fa-user"></i>
                                </div>
                              </div>
                              <div className="ml-3">
                                <span
                                  className={`px-0 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                    isDarkMode
                                      ? "text-gray-200"
                                      : "text-gray-700"
                                  }`}
                                >
                                  {capitalizeFirstLetter(item.nom)}{" "}
                                  {capitalizeFirstLetter(item.postnom)}{" "}
                                  {capitalizeFirstLetter(item.prenom)}
                                </span>{" "}
                                <div
                                  className={`text-sm ${
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-500"
                                  }`}
                                >
                                  <span className="text-[11px] mr-1.5 opacity-60 ">
                                    ID: PAR-00{index + 1}
                                  </span>
                                  {statusPill(
                                    !!item.status,
                                    "Actif",
                                    "Inactif",
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap">
                            {item.telephone ? item.telephone : "-"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                !!item.students.length
                                  ? isDarkMode
                                    ? "bg-green-900/40 text-green-400"
                                    : "bg-green-100 text-green-800"
                                  : isDarkMode
                                    ? "bg-gray-800 text-gray-400"
                                    : "bg-gray-100 text-gray-800"
                              } `}
                            >
                              {!!item.students.length
                                ? `${item.students.length} enfants`
                                : "Aucun"}
                            </span>
                          </td>

                          <td className="w-[116px] min-w-[116px] px-4 py-3 whitespace-nowrap text-center">
                            <div className="flex justify-center items-center gap-1 opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto">
                              {/* <button className="text-blue-500 hover:text-blue-700 cursor-pointer !rounded-button whitespace-nowrap">
                              <i className="fas fa-eye"></i>
                            </button> */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditForParent(item);
                                }}
                                title="Modifier le parent"
                                aria-label="Modifier le parent"
                                className={parentActionClass("blue")}
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  item.status
                                    ? dispatch({
                                        type: "DESACTIVATE",
                                        payload: {
                                          actionText:
                                            "Êtes-vous sûr de vouloir désactiver ce parent ?",
                                          currentId: item.parent_id,
                                          actionSection: "parent",
                                        },
                                      })
                                    : dispatch({
                                        type: "ACTIVATE",
                                        payload: {
                                          actionText:
                                            "Êtes-vous sûr de vouloir activer ce parent ?",
                                          currentId: item.parent_id,
                                          actionSection: "parent",
                                        },
                                      });
                                }}
                                className={`${
                                  item.status
                                    ? "text-green-500 hover:text-green-700"
                                    : "text-gray-500 hover:text-green-700"
                                } ${parentActionClass("green")}`}
                              >
                                <i className="fa-solid fa-circle-check"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              <div
                className={`mt-3 flex items-center justify-end rounded-lg border px-3 py-2 ${isDarkMode ? "border-[#30363D] bg-[#161B22]/60" : "border-gray-200 bg-gray-50/60"}`}
              >
                <div
                  className={`text-sm ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {filteredParents.length} parent
                  {filteredParents.length > 1 ? "s" : ""} affiché
                  {filteredParents.length > 1 ? "s" : ""} sur{" "}
                  {updateParentsArr.length}
                </div>
              </div>
            </div>
          </div>

          {/* Activate dialog */}
          {state_actions.actionType === "activate" && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div
                className={`${
                  isDarkMode ? "bg-[#161B22]" : "bg-white"
                } rounded-2xl shadow-2xl w-full max-w-md p-5 sm:p-6 border border-transparent`}
              >
                <div className="text-lg font-semibold mb-3">
                  Confirmer l'activation
                </div>
                <p
                  className={`${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  } mb-6`}
                >
                  {state_actions.actionText}
                </p>
                <div className="flex flex-wrap justify-end gap-2">
                  <button
                    disabled={mutationActivate.isPending}
                    onClick={() =>
                      dispatch({
                        type: "CANCEL",
                        payload: {
                          actionText: "",
                          currentId: "",
                          actionSection: "",
                        },
                      })
                    }
                    className={`px-4 py-2 rounded-lg ${
                      isDarkMode
                        ? "bg-transparent border border-gray-600 text-gray-300 hover:bg-[#21262D]"
                        : "bg-transparent border border-gray-200 text-gray-600 hover:bg-gray-50"
                    } !rounded-button whitespace-nowrap ${
                      mutationActivate.isPending
                        ? "cursor-not-allowed opacity-60"
                        : ""
                    }`}
                  >
                    Annuler
                  </button>
                  <button
                    disabled={mutationActivate.isPending}
                    onClick={handleActivate}
                    className={`px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white shadow-sm !rounded-button whitespace-nowrap ${
                      mutationActivate.isPending
                        ? "cursor-not-allowed opacity-70"
                        : ""
                    }`}
                  >
                    {mutationActivate.isPending ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="fas fa-spinner fa-spin"></i>
                        Activation en cours...
                      </span>
                    ) : (
                      "Confirmer l'activation"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Desactivate dialog */}
          {state_actions.actionType === "desactivate" && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div
                className={`${
                  isDarkMode ? "bg-[#161B22]" : "bg-white"
                } rounded-2xl shadow-2xl w-full max-w-md p-5 sm:p-6 border border-transparent`}
              >
                <div className="text-lg font-semibold mb-3">
                  Confirmer la désactivation
                </div>
                <p
                  className={`${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  } mb-6`}
                >
                  {state_actions.actionText}
                </p>
                <div className="flex flex-wrap justify-end gap-2">
                  <button
                    disabled={mutationDesactivate.isPending}
                    onClick={() =>
                      dispatch({
                        type: "CANCEL",
                        payload: {
                          actionText: "",
                          currentId: "",
                          actionSection: "",
                        },
                      })
                    }
                    className={`px-4 py-2 rounded-lg ${
                      isDarkMode
                        ? "bg-transparent border border-gray-600 text-gray-300 hover:bg-[#21262D]"
                        : "bg-transparent border border-gray-200 text-gray-600 hover:bg-gray-50"
                    } !rounded-button whitespace-nowrap ${
                      mutationDesactivate.isPending
                        ? "cursor-not-allowed opacity-60"
                        : ""
                    }`}
                  >
                    Annuler
                  </button>
                  <button
                    disabled={mutationDesactivate.isPending}
                    onClick={handleDesactivate}
                    className={`px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white shadow-sm !rounded-button whitespace-nowrap ${
                      mutationDesactivate.isPending
                        ? "cursor-not-allowed opacity-70"
                        : ""
                    }`}
                  >
                    {mutationDesactivate.isPending ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="fas fa-spinner fa-spin"></i>
                        Désactivation en cours...
                      </span>
                    ) : (
                      "Confirmer la désactivation"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ===== Détail parent + enfants (au clic sur une ligne) ===== */}
          {showDetailModal && selectedParent && (
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
              onClick={closeDetailModal}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                className={`${
                  isDarkMode ? "bg-[#161B22]" : "bg-white"
                } rounded-2xl border border-transparent shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden`}
              >
                {/* Header */}
                <div
                  className={`px-5 py-4 border-b flex-shrink-0 ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  } flex justify-between items-start`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isDarkMode
                          ? "bg-blue-500/10 text-blue-400"
                          : "bg-blue-50 text-blue-600"
                      }`}
                    >
                      <i className="fas fa-user text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold leading-tight">
                        {capitalizeFirstLetter(selectedParent.nom)}{" "}
                        {capitalizeFirstLetter(selectedParent.postnom)}{" "}
                        {capitalizeFirstLetter(selectedParent.prenom)}
                      </h3>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span
                          className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${
                            isDarkMode
                              ? "bg-[#30363D] text-gray-300"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {parentTypeLabel[selectedParent.type] ??
                            selectedParent.type ??
                            "-"}
                        </span>
                        <span
                          className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${
                            selectedParent.status
                              ? isDarkMode
                                ? "bg-blue-900/40 text-blue-400"
                                : "bg-blue-100 text-blue-800"
                              : isDarkMode
                                ? "bg-red-900/40 text-red-400"
                                : "bg-red-100 text-red-800"
                          }`}
                        >
                          {selectedParent.status ? "Actif" : "Inactif"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={closeDetailModal}
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors flex-shrink-0 ${
                      isDarkMode
                        ? "text-gray-400 hover:bg-[#21262D] hover:text-gray-200"
                        : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    }`}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
                  {/* Coordonnées */}
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <i
                        className={`fas fa-address-book text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      ></i>
                      <h4
                        className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Coordonnées
                      </h4>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3">
                      <DetailField
                        label="Genre"
                        value={
                          selectedParent.sexe === "M"
                            ? "Masculin"
                            : selectedParent.sexe === "F"
                              ? "Féminin"
                              : "-"
                        }
                        isDarkMode={isDarkMode}
                      />
                      <DetailField
                        label="Téléphone"
                        value={selectedParent.telephone}
                        icon="fa-phone"
                        isDarkMode={isDarkMode}
                      />
                      <DetailField
                        label="Email"
                        value={selectedParent.email}
                        icon="fa-envelope"
                        isDarkMode={isDarkMode}
                      />
                      <DetailField
                        label="Profession"
                        value={selectedParent.profession}
                        icon="fa-briefcase"
                        isDarkMode={isDarkMode}
                      />
                    </div>
                  </section>

                  <hr
                    className={
                      isDarkMode ? "border-[#30363D]" : "border-gray-100"
                    }
                  />

                  {/* Adresse */}
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <i
                        className={`fas fa-map-marker-alt text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      ></i>
                      <h4
                        className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Adresse principale
                      </h4>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3">
                      <DetailField
                        label="Avenue"
                        value={selectedParent.avenue}
                        isDarkMode={isDarkMode}
                      />
                      <DetailField
                        label="Quartier"
                        value={selectedParent.quartier}
                        isDarkMode={isDarkMode}
                      />
                      <DetailField
                        label="Commune"
                        value={selectedParent.commune}
                        isDarkMode={isDarkMode}
                      />
                      <DetailField
                        label="Ville"
                        value={selectedParent.ville}
                        isDarkMode={isDarkMode}
                      />
                    </div>
                  </section>

                  <hr
                    className={
                      isDarkMode ? "border-[#30363D]" : "border-gray-100"
                    }
                  />

                  {/* Enfants */}
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <i
                        className={`fas fa-child text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      ></i>
                      <h4
                        className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Enfants ({selectedParent.students?.length ?? 0})
                      </h4>
                    </div>

                    {selectedParent.students?.length ? (
                      <div className="space-y-2">
                        {selectedParent.students.map(
                          (child: any, i: number) => {
                            const age = child.date_naissance
                              ? Math.floor(
                                  (Date.now() -
                                    new Date(child.date_naissance).getTime()) /
                                    (365.25 * 24 * 60 * 60 * 1000),
                                )
                              : null;

                            return (
                              <div
                                key={child.eleve_id ?? i}
                                className={`p-3 rounded-lg border ${
                                  isDarkMode
                                    ? "bg-[#0D1117] border-[#30363D]"
                                    : "bg-gray-50 border-gray-200"
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <div
                                    className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                                      isDarkMode
                                        ? "bg-[#30363D]"
                                        : "bg-gray-200"
                                    }`}
                                  >
                                    <i
                                      className={`fas ${
                                        child.sexe === "F"
                                          ? "fa-child-dress"
                                          : "fa-child"
                                      } text-xs`}
                                    ></i>
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                      <p
                                        className={`text-sm font-medium truncate ${
                                          isDarkMode
                                            ? "text-gray-100"
                                            : "text-gray-900"
                                        }`}
                                      >
                                        {capitalizeFirstLetter(child.nom)}{" "}
                                        {capitalizeFirstLetter(child.postnom)}{" "}
                                        {capitalizeFirstLetter(child.prenom)}
                                      </p>
                                      <span
                                        className={`flex-shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${
                                          child.status
                                            ? isDarkMode
                                              ? "bg-blue-900/40 text-blue-400"
                                              : "bg-blue-100 text-blue-800"
                                            : isDarkMode
                                              ? "bg-red-900/40 text-red-400"
                                              : "bg-red-100 text-red-800"
                                        }`}
                                      >
                                        {child.status ? "Actif" : "Inactif"}
                                      </span>
                                    </div>
                                    <p
                                      className={`text-xs mt-0.5 ${
                                        isDarkMode
                                          ? "text-gray-500"
                                          : "text-gray-400"
                                      }`}
                                    >
                                      {child.sexe === "F"
                                        ? "Féminin"
                                        : child.sexe === "M"
                                          ? "Masculin"
                                          : "-"}
                                      {age !== null && ` · ${age} ans`}
                                      {child.lieu_naissance &&
                                        ` · né(e) à ${capitalizeFirstLetter(child.lieu_naissance)}`}
                                    </p>
                                  </div>
                                </div>
                                {(child.telephone ||
                                  child.ville ||
                                  child.commune) && (
                                  <div
                                    className={`flex flex-wrap gap-x-4 gap-y-1 mt-2 pt-2 border-t text-xs ${
                                      isDarkMode
                                        ? "border-[#30363D] text-gray-400"
                                        : "border-gray-200 text-gray-500"
                                    }`}
                                  >
                                    {child.telephone && (
                                      <span className="flex items-center gap-1">
                                        <i className="fas fa-phone text-[10px] opacity-70"></i>
                                        {child.telephone}
                                      </span>
                                    )}
                                    {(child.commune || child.ville) && (
                                      <span className="flex items-center gap-1">
                                        <i className="fas fa-map-marker-alt text-[10px] opacity-70"></i>
                                        {[child.commune, child.ville]
                                          .filter(Boolean)
                                          .map((v) => capitalizeFirstLetter(v))
                                          .join(", ")}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          },
                        )}
                      </div>
                    ) : (
                      <div
                        className={`text-sm rounded-lg border border-dashed p-4 text-center ${
                          isDarkMode
                            ? "border-[#30363D] text-gray-500"
                            : "border-gray-200 text-gray-400"
                        }`}
                      >
                        Aucun enfant enregistré pour ce parent.
                      </div>
                    )}
                  </section>
                </div>

                {/* Footer */}
                <div
                  className={`px-5 py-3 border-t flex-shrink-0 ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  } flex justify-end gap-3`}
                >
                  <button
                    onClick={closeDetailModal}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors !rounded-button whitespace-nowrap ${
                      isDarkMode
                        ? "bg-transparent border border-gray-600 text-gray-300 hover:bg-[#21262D]"
                        : "bg-transparent border border-gray-200 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    Fermer
                  </button>
                  <button
                    onClick={() => {
                      openEditForParent(selectedParent);
                      closeDetailModal();
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors shadow-sm !rounded-button whitespace-nowrap ${
                      isDarkMode
                        ? "bg-blue-600 hover:bg-blue-700"
                        : "bg-blue-500 hover:bg-blue-600"
                    }`}
                  >
                    <span className="flex items-center justify-center gap-2">
                      <i className="fas fa-edit"></i>
                      Modifier
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Edit && Add personal Modal */}
          {(showEditModal || showAddModal) && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div
                className={`${
                  isDarkMode ? "bg-[#161B22]" : "bg-white"
                } rounded-2xl border border-transparent shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden`}
              >
                {/* Header */}
                <div
                  className={`px-5 py-4 border-b flex-shrink-0 ${
                    isDarkMode
                      ? "border-[#30363D] bg-[#161B22]"
                      : "border-gray-200 bg-white"
                  } flex justify-between items-center`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isDarkMode
                          ? "bg-blue-500/10 text-blue-400"
                          : "bg-blue-50 text-blue-600"
                      }`}
                    >
                      <i
                        className={
                          showEditModal
                            ? "fas fa-user-edit"
                            : "fas fa-user-plus"
                        }
                      ></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold leading-tight">
                        {showEditModal
                          ? "Modifier parent"
                          : "Ajouter un nouveau parent"}
                      </h3>
                      <p
                        className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      >
                        Les champs marqués d'un * sont obligatoires
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      showEditModal
                        ? setShowEditModal(false)
                        : setShowAddModal(false);
                    }}
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                      isDarkMode
                        ? "text-gray-400 hover:bg-[#21262D] hover:text-gray-200"
                        : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    }`}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>

                {/* Form */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
                  {/* Section: Identité */}
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <i
                        className={`fas fa-id-card text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      ></i>
                      <h4
                        className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Identité
                      </h4>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <label
                          htmlFor="nom"
                          className={`block text-sm font-medium mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Nom <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="nom"
                          name="nom"
                          required
                          value={
                            showEditModal ? formEditData.nom : formNewData.nom
                          }
                          onChange={handleChange}
                          className={`block w-full border rounded-lg shadow-sm py-2 px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                              : "bg-white border-gray-200 text-gray-900"
                          }`}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="postnom"
                          className={`block text-sm font-medium mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Post-Nom <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="postnom"
                          name="postnom"
                          required
                          value={
                            showEditModal
                              ? formEditData.postnom
                              : formNewData.postnom
                          }
                          onChange={handleChange}
                          className={`block w-full border rounded-lg shadow-sm py-2 px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                              : "bg-white border-gray-200 text-gray-900"
                          }`}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="prenom"
                          className={`block text-sm font-medium mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Prénom <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="prenom"
                          name="prenom"
                          required
                          value={
                            showEditModal
                              ? formEditData.prenom
                              : formNewData.prenom
                          }
                          onChange={handleChange}
                          className={`block w-full border rounded-lg shadow-sm py-2 px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                              : "bg-white border-gray-200 text-gray-900"
                          }`}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
                      <div>
                        <label
                          htmlFor="type"
                          className={`block text-sm font-medium mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Type de parenté{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <select
                          id="type"
                          name="type"
                          required
                          value={
                            showEditModal ? formEditData.type : formNewData.type
                          }
                          onChange={(e) => {
                            showEditModal
                              ? setformEditData((prev) => ({
                                  ...prev,
                                  type: e.target.value,
                                }))
                              : setformNewData((prev) => ({
                                  ...prev,
                                  type: e.target.value,
                                }));
                          }}
                          className={`block w-full border rounded-lg shadow-sm py-2 px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                              : "bg-white border-gray-200 text-gray-900"
                          }`}
                        >
                          <option value="">Sélectionner</option>
                          <option value="pere">Père</option>
                          <option value="mere">Mère</option>
                          <option value="tuteur">Tuteur légal</option>
                          <option value="autre">Autre</option>
                        </select>
                      </div>
                      <div>
                        <label
                          htmlFor="genre"
                          className={`block text-sm font-medium mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Genre <span className="text-red-500">*</span>
                        </label>
                        <select
                          id="genre"
                          name="genre"
                          required
                          value={
                            showEditModal ? formEditData.sexe : formNewData.sexe
                          }
                          onChange={(e) => {
                            showEditModal
                              ? setformEditData((prev) => ({
                                  ...prev,
                                  sexe: e.target.value,
                                }))
                              : setformNewData((prev) => ({
                                  ...prev,
                                  sexe: e.target.value,
                                }));
                          }}
                          className={`block w-full border rounded-lg shadow-sm py-2 px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                              : "bg-white border-gray-200 text-gray-900"
                          }`}
                        >
                          <option value="">Sélectionner</option>
                          <option value="M">Masculin</option>
                          <option value="F">Féminin</option>
                        </select>
                      </div>
                    </div>
                  </section>

                  <hr
                    className={
                      isDarkMode ? "border-[#30363D]" : "border-gray-100"
                    }
                  />

                  {/* Section: Adresse */}
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <i
                        className={`fas fa-map-marker-alt text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      ></i>
                      <h4
                        className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Adresse principale
                      </h4>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-6 gap-5">
                      <div className="sm:col-span-4">
                        <label
                          htmlFor="avenue"
                          className={`block text-sm font-medium mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Avenue <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="avenue"
                          id="avenue"
                          required
                          value={
                            showEditModal
                              ? formEditData.avenue
                              : formNewData.avenue
                          }
                          onChange={handleChange}
                          className={`block w-full border rounded-lg shadow-sm py-2 px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                              : "bg-white border-gray-200 text-gray-900"
                          }`}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label
                          htmlFor="quartier"
                          className={`block text-sm font-medium mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Quartier <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="quartier"
                          id="quartier"
                          required
                          value={
                            showEditModal
                              ? formEditData.quartier
                              : formNewData.quartier
                          }
                          onChange={handleChange}
                          className={`block w-full border rounded-lg shadow-sm py-2 px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                              : "bg-white border-gray-200 text-gray-900"
                          }`}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label
                          htmlFor="commune"
                          className={`block text-sm font-medium mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Commune <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="commune"
                          id="commune"
                          required
                          value={
                            showEditModal
                              ? formEditData.commune
                              : formNewData.commune
                          }
                          onChange={handleChange}
                          className={`block w-full border rounded-lg shadow-sm py-2 px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                              : "bg-white border-gray-200 text-gray-900"
                          }`}
                        />
                      </div>
                      <div className="sm:col-span-4">
                        <label
                          htmlFor="ville"
                          className={`block text-sm font-medium mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Ville <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="ville"
                          id="ville"
                          required
                          value={
                            showEditModal
                              ? formEditData.ville
                              : formNewData.ville
                          }
                          onChange={handleChange}
                          className={`block w-full border rounded-lg shadow-sm py-2 px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                              : "bg-white border-gray-200 text-gray-900"
                          }`}
                        />
                      </div>
                    </div>
                  </section>

                  <hr
                    className={
                      isDarkMode ? "border-[#30363D]" : "border-gray-100"
                    }
                  />

                  {/* Section: Coordonnées */}
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <i
                        className={`fas fa-address-book text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      ></i>
                      <h4
                        className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Coordonnées
                      </h4>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <label
                          htmlFor="profession"
                          className={`block text-sm font-medium mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Profession <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="profession"
                          name="profession"
                          value={
                            showEditModal
                              ? formEditData.profession
                              : formNewData.profession
                          }
                          onChange={handleChange}
                          className={`block w-full border rounded-lg shadow-sm py-2 px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                              : "bg-white border-gray-200 text-gray-900"
                          }`}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="email"
                          className={`block text-sm font-medium mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Email
                        </label>
                        <input
                          type="email"
                          name="email"
                          id="email"
                          required
                          value={
                            showEditModal
                              ? formEditData.email
                              : formNewData.email
                          }
                          onChange={handleChange}
                          className={`block w-full border rounded-lg shadow-sm py-2 px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                              : "bg-white border-gray-200 text-gray-900"
                          }`}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="telephone"
                          className={`block text-sm font-medium mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Téléphone <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          name="telephone"
                          id="telephone"
                          required
                          value={
                            showEditModal
                              ? formEditData.telephone
                              : formNewData.telephone
                          }
                          onChange={handleChange}
                          className={`block w-full border rounded-lg shadow-sm py-2 px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                              : "bg-white border-gray-200 text-gray-900"
                          }`}
                        />
                      </div>
                    </div>
                  </section>
                </div>

                {/* Footer */}
                <div
                  className={`px-5 py-3 border-t flex-shrink-0 ${
                    isDarkMode
                      ? "border-[#30363D] bg-[#161B22]"
                      : "border-gray-200 bg-white"
                  } flex justify-end gap-3`}
                >
                  <button
                    disabled={
                      mutationUpdate.isPending || mutationCreate.isPending
                    }
                    onClick={() => {
                      showEditModal
                        ? setShowEditModal(false)
                        : setShowAddModal(false);
                      showEditModal
                        ? setformEditData({
                            id: null,
                            nom: "",
                            postnom: "",
                            prenom: "",
                            sexe: "",
                            ville: "",
                            commune: "",
                            quartier: "",
                            avenue: "",
                            telephone: "",
                            email: "",
                            profession: "",
                            type: "",
                          })
                        : setformNewData({
                            nom: "",
                            postnom: "",
                            prenom: "",
                            sexe: "",
                            ville: "",
                            commune: "",
                            quartier: "",
                            avenue: "",
                            telephone: "",
                            email: "",
                            profession: "",
                            type: "",
                          });
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors !rounded-button whitespace-nowrap ${
                      isDarkMode
                        ? "bg-transparent border border-gray-600 text-gray-300 hover:bg-[#21262D]"
                        : "bg-transparent border border-gray-200 text-gray-600 hover:bg-gray-50"
                    } ${
                      mutationUpdate.isPending || mutationCreate.isPending
                        ? "cursor-not-allowed opacity-60"
                        : ""
                    }`}
                  >
                    Annuler
                  </button>

                  <button
                    disabled={
                      mutationUpdate.isPending || mutationCreate.isPending
                    }
                    onClick={() => {
                      showEditModal ? handleUpdate() : handleCreate();
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors shadow-sm !rounded-button whitespace-nowrap ${
                      isDarkMode
                        ? "bg-blue-600 hover:bg-blue-700"
                        : "bg-blue-500 hover:bg-blue-600"
                    } ${
                      mutationUpdate.isPending || mutationCreate.isPending
                        ? "cursor-not-allowed opacity-70"
                        : ""
                    }`}
                  >
                    {mutationUpdate.isPending || mutationCreate.isPending ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="fas fa-spinner fa-spin"></i>
                        Chargement...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <i className="fas fa-check"></i>
                        {showEditModal ? "Mettre à jour" : "Ajouter"}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ===== NOTIFICATION ===== */}
          {notification && (
            <div
              className={`fixed bottom-4 text-[14px] right-4 max-w-[calc(100vw-2rem)] px-4 py-3 z-[60] rounded-xl text-white shadow-lg transition-all duration-500 ${
                notification.type === "success" ? "bg-green-500" : "bg-red-500"
              }`}
            >
              {notification.message}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Parent;
